import React from 'react'
import { Box, SvgIcon, Tooltip } from '@mui/material'
import { dsColorNew, dataColors } from '@palette'

/**
 * Renders a small silhouette badge per ACRISS code.
 *
 * ACRISS positions:
 *   L1: size class    (M=Mini, E=Economy, C=Compact, I=Intermediate,
 *                      S=Standard, F=Fullsize, P=Premium, L=Luxury,
 *                      U=Premium, X=Special, O=Open / non-standard)
 *   L2: body type     (D=4-door, C=2-door, L=Limousine, T=Convertible,
 *                      V=Van, F=SUV, W=Wagon/Estate, J=Open Air, R=RV,
 *                      P/Q=Pickup, S=Sport, X=Special)
 *   L3: transmission  (A=Automatic, M=Manual)
 *   L4: fuel/AC       (R=Gas w/AC, …)
 *
 * The body type (L2) drives the silhouette; the size class (L1) drives
 * a small colour band so Compact vs Luxury read differently at a glance.
 */
const SIZE_PALETTE = {
	M: dataColors.slate400, // Mini      — pale grey
	E: dataColors.slate400, // Economy
	C: dataColors.slate500, // Compact
	I: dataColors.slate600, // Intermediate
	S: dataColors.slate600, // Standard
	F: dataColors.sky500, // Fullsize
	P: dataColors.violet600, // Premium   — violet
	L: dataColors.violet600, // Luxury
	U: dataColors.violet600, // Premium (intl)
	X: dsColorNew.warningBar, // Special   — amber
	O: dataColors.sky500, // Open / non-standard catch-all
	W: dataColors.sky500,
}

function SedanIcon(props) {
	return (
		<SvgIcon {...props} viewBox="0 0 24 24">
			<path d="M19 11l-1.5-4.5A2 2 0 0 0 15.6 5H8.4a2 2 0 0 0-1.9 1.5L5 11H3v6h2a2 2 0 0 0 4 0h6a2 2 0 0 0 4 0h2v-6h-2zM7.6 7h8.8l1 3H6.6l1-3zM7 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm10 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
		</SvgIcon>
	)
}

function SUVIcon(props) {
	return (
		<SvgIcon {...props} viewBox="0 0 24 24">
			<path d="M20 10V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3H2v8h2a2 2 0 0 0 4 0h8a2 2 0 0 0 4 0h2v-8h-2zM6 7h12v3H6V7zm0 10a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm12 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
		</SvgIcon>
	)
}

function VanIcon(props) {
	return (
		<SvgIcon {...props} viewBox="0 0 24 24">
			<path d="M22 9.5L20 6H4a2 2 0 0 0-2 2v9h2a2 2 0 0 0 4 0h8a2 2 0 0 0 4 0h2V11l-2-1.5zM4 8h11v3H4V8zm3 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm12 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm-2-6h2.5l1.5 2H17v-2z"/>
		</SvgIcon>
	)
}

function ConvertibleIcon(props) {
	return (
		<SvgIcon {...props} viewBox="0 0 24 24">
			<path d="M19 12l-1-3.5A2 2 0 0 0 16.1 7H7.9a2 2 0 0 0-1.9 1.5L5 12H3v5h2a2 2 0 0 0 4 0h6a2 2 0 0 0 4 0h2v-5h-2zM4 6h16v1H4z" opacity=".55"/>
			<path d="M7 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm10 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
		</SvgIcon>
	)
}

function WagonIcon(props) {
	return (
		<SvgIcon {...props} viewBox="0 0 24 24">
			<path d="M21 11l-1.5-4A2 2 0 0 0 17.6 6H6.4a2 2 0 0 0-1.9 1.5L3 12H2v5h2a2 2 0 0 0 4 0h8a2 2 0 0 0 4 0h2v-6h-1zM6.6 8h11l1 3h-13l1-3zM6 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm12 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
		</SvgIcon>
	)
}

const BODY_ICON = {
	D: SedanIcon,        // 4-door sedan
	C: SedanIcon,        // 2-door coupé — close enough
	L: SedanIcon,        // Limousine
	T: ConvertibleIcon,  // Convertible
	V: VanIcon,          // Passenger van / minivan
	F: SUVIcon,          // SUV
	J: SUVIcon,          // Open air all-terrain
	W: WagonIcon,        // Estate / wagon
	S: SedanIcon,        // Sport — keep sedan
	X: SedanIcon,        // Special
	P: VanIcon,          // Pickup — approximation
	Q: VanIcon,          // Pickup ext.cab — approximation
	R: VanIcon,          // RV — approximation
}

export default function AcrissIcon({ sipp, size = 26, withTooltip = true }) {
	const code = (sipp || '').toUpperCase()
	const L1 = code[0] || ''
	const L2 = code[1] || ''
	const Icon  = BODY_ICON[L2] || SedanIcon
	const color = SIZE_PALETTE[L1] || dataColors.slate500
	const node = (
		<Box sx={{
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			width: size, height: size, borderRadius: '50%',
			bgcolor: color, color: dsColorNew.white, flexShrink: 0,
		}}>
			<Icon sx={{ fontSize: size * 0.62 }}/>
		</Box>
	)
	if (!withTooltip) return node
	return (
		<Tooltip arrow placement="top" title={tooltipFor(code)}>
			{node}
		</Tooltip>
	)
}

function tooltipFor(code) {
	if (!code) return ''
	const sizeMap = {
		M: 'Mini', E: 'Economy', C: 'Compact', I: 'Intermediate',
		S: 'Standard', F: 'Fullsize', P: 'Premium', L: 'Luxury',
		U: 'Premium', X: 'Special', O: 'Open',
	}
	const bodyMap = {
		D: '4-door', C: '2-door', L: 'Limousine', T: 'Convertible',
		V: 'Van', F: 'SUV', W: 'Estate', J: 'Open-air SUV', S: 'Sport',
		P: 'Pickup', Q: 'Pickup ext.', R: 'RV', X: 'Special',
	}
	const sz = sizeMap[code[0]] || '?'
	const bd = bodyMap[code[1]] || '?'
	const tx = code[2] === 'A' ? 'Automatic' : code[2] === 'M' ? 'Manual' : ''
	return `${code} · ${sz}, ${bd}${tx ? ', ' + tx : ''}`
}
